import { forwardRef, Children, cloneElement, useRef, useMemo } from 'react';
import type {
  ComponentProps,
  ForwardRefExoticComponent,
  ReactElement,
  RefAttributes,
  KeyboardEvent,
} from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';

import { Dropdown } from './Dropdown';
import { DropdownItem } from './DropdownItem';
import { DropdownLinkItem } from './DropdownLinkItem';
import { useTippy } from '@hooks/useTippy';
import type { ButtonImperativeHandleRef } from './dropdown-menu-models';
import { DropdownBaseItem } from './DropdownBaseItem';
import { DropdownSection } from './DropdownSection';

export type DropdownItems =
  | ReactElement<ComponentProps<typeof DropdownItem>, 'DropdownItem'>[]
  | ReactElement<ComponentProps<typeof DropdownItem>, 'DropdownItem'>
  | ReactElement<ComponentProps<typeof DropdownLinkItem>, 'DropdownLinkItem'>[]
  | ReactElement<ComponentProps<typeof DropdownLinkItem>, 'DropdownLinkItem'>
  | ReactElement<ComponentProps<typeof DropdownBaseItem>, 'DropdownBaseItem'>[]
  | ReactElement<ComponentProps<typeof DropdownBaseItem>, 'DropdownBaseItem'>;

export interface DropdownMenuProps
  extends Omit<
    ComponentProps<typeof Dropdown>,
    'children' | 'trigger' | 'ref'
  > {
  /**
   * List of DropdownMenu.Item components.
   */
  children:
    | DropdownItems
    | ReactElement<ComponentProps<typeof DropdownSection>, 'DropdownSection'>[]
    | ReactElement<ComponentProps<typeof DropdownSection>, 'DropdownSection'>;
  /**
   * Element to trigger the DropdownMenu. Must have an accessible `ref`.
   */
  trigger: ReactElement;
  /**
   * Size of the dropdown. 200/280/344px.
   * Defaults to 's'
   */
  size?: 's' | 'm' | 'l';
}

export interface CompoundedDropdownMenu
  extends ForwardRefExoticComponent<
    DropdownMenuProps & RefAttributes<HTMLInputElement>
  > {
  BaseItem: typeof DropdownBaseItem;
  Item: typeof DropdownItem;
  LinkItem: typeof DropdownLinkItem;
  Section: typeof DropdownSection;
}

const DROPDOWN_WIDTH: Record<DropdownMenuProps['size'], number> = {
  s: 200,
  m: 280,
  l: 344,
};

const StyledDropdown = styled(Dropdown)<Pick<DropdownMenuProps, 'size'>>`
  width: ${({ size }) => DROPDOWN_WIDTH[size]}px;
  max-height: 440px;
  padding: ${tokens.spacer.x1} 0;
  list-style: none;

  /* TODO: Remove this override when insights styles are deprecated */
  .tippy-content {
    padding: 0;
  }
  /* end override */
`;

const ItemList = styled('ul')`
  list-style: none;
  margin: 0;
  padding: 0;
`;

interface FindAdjacentItemIndexArgs {
  children: ReactElement[];
  currentIndex: number;
  direction: 'previous' | 'next';
}

function findAdjacentItemIndex({
  children,
  currentIndex,
  direction,
}: FindAdjacentItemIndexArgs): number {
  if (children.length === 1) {
    return currentIndex;
  }

  let newIndex = currentIndex;
  let isFound = false;
  do {
    if (direction === 'previous') {
      const isFirstElement = newIndex === 0;
      newIndex = isFirstElement ? children.length - 1 : newIndex - 1;
    } else {
      const isLastElement = newIndex >= children.length - 1;
      newIndex = isLastElement ? 0 : newIndex + 1;
    }
    if (
      !children[newIndex].props.isDisabled &&
      !children[newIndex].props.isHidden
    ) {
      isFound = true;
    }
  } while (!isFound);

  return newIndex;
}

/**
 * DropdownMenu can be used with DropdownMenu.Item to build a popover menu of actions.
 *
 * {@link https://zeroheight.com/76c439891/p/96bb9e-dropdown-menu/b/72fb4d Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-dropdownmenu Code documentation}
 *
 * @example
 * <DropdownMenu trigger={<IconButton />}>
 *   <DropdownMenu.Item>Item</DropdownMenu.Item>
 * </DropdownMenu>
 */
// ESLint gets confused by the TS type casting that's required to expose the Item
// eslint-disable-next-line react/display-name
export const DropdownMenu = forwardRef<HTMLElement, DropdownMenuProps>(
  ({ children, trigger, size = 's', ...props }: DropdownMenuProps, ref) => {
    const tippyInstance = useTippy();
    const focusIndexRef = useRef<number>(-1);
    const childrenRefs = useRef<ButtonImperativeHandleRef[]>([]);

    const focusChild = (childIndex: number) => {
      const childRef = childrenRefs.current[childIndex];
      focusIndexRef.current = childIndex;

      if (childRef) {
        childRef.focus();
      }
    };

    // flat the array of dropdown items if DropdownSection were use
    const dropdownItemsList = useMemo(() => {
      return Children.toArray(children).reduce<ReactElement[]>(
        (list, childItem: ReactElement) => {
          if (childItem.type === DropdownSection) {
            return list.concat(childItem.props.children);
          } else {
            list.push(childItem);
            return list;
          }
        },
        [],
      );
    }, [children]);

    const mapChildren = () => {
      let childIndex = -1;
      const cloneElements = (childrenList) =>
        Children.map(childrenList, (child: ReactElement) => {
          if (
            child.type === DropdownItem ||
            child.type === DropdownBaseItem ||
            child.type === DropdownLinkItem
          ) {
            childIndex++;
            return cloneElement(child, {
              ...child.props,
              ref: (childRef: ButtonImperativeHandleRef) => {
                childrenRefs.current.push(childRef);
              },
              tabIndex: focusIndexRef.current === childIndex ? '0' : '-1',
              onClick: (e) => {
                tippyInstance.close();
                if (child.props.onClick) {
                  child.props.onClick(e);
                }
              },
              onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => {
                const shouldGoToPrevItem = event.key === 'ArrowUp';
                const shouldGoToNextItem = event.key === 'ArrowDown';
                const shouldCancel = event.key === 'Escape';
                const isChangingFocus = event.key === 'Tab';

                if (shouldGoToPrevItem || shouldGoToNextItem) {
                  // stop propagation of the event so the page is not scrolled in the direction pressed
                  event.preventDefault();

                  const newFocusIndex = findAdjacentItemIndex({
                    children: dropdownItemsList,
                    currentIndex: focusIndexRef.current,
                    direction: shouldGoToNextItem ? 'next' : 'previous',
                  });
                  focusChild(newFocusIndex);
                } else if (shouldCancel) {
                  tippyInstance.close();
                  tippyInstance.focusTriggerElement();
                } else if (isChangingFocus) {
                  // We don't want to change the focus by tab since
                  // tippy menu gets rendered in a different part of the DOM,
                  // so switching element with Tab would put the focus in a different part of the website.
                  // If the user wants to go back to the regular tab index, they have to close the menu with Esc.
                  event.preventDefault();
                }
              },
            });
          }
          if (child.type === DropdownSection) {
            return (
              <DropdownSection title={child.props.title}>
                {cloneElements(child.props.children)}
              </DropdownSection>
            );
          }
          return null;
        });

      return cloneElements(children);
    };

    const enhancedTrigger = cloneElement(trigger, {
      ...trigger.props,
      onKeyDown: (event) => {
        const shouldCancel =
          tippyInstance.state.isVisible && event.key === 'Escape';

        const isOpeningKey = ['Enter', ' '].includes(event.key);
        const shouldGoToNextItem = event.key === 'ArrowDown';

        const shouldAutoFocusFirstOption =
          !tippyInstance.state.isVisible && isOpeningKey;
        const shouldFocusNextOption =
          tippyInstance.state.isVisible && shouldGoToNextItem;

        const firstAvailableChildIndex = dropdownItemsList.findIndex(
          (child: ReactElement) =>
            !child.props.isDisabled && !child.props.isHidden,
        );

        if (shouldCancel) {
          tippyInstance.close();
        } else if (shouldAutoFocusFirstOption) {
          // delay the focus child a bit until the dropdown menu animation is completed
          setTimeout(() => {
            focusChild(firstAvailableChildIndex);
          }, 100);
        } else if (shouldFocusNextOption) {
          // stop propagation of the event so the page is not scrolled in the direction pressed
          event.preventDefault();
          focusChild(firstAvailableChildIndex);
        }
      },
    });

    const handleOnHide = () => {
      focusChild(-1);
    };

    return (
      <StyledDropdown
        {...props}
        onCreate={tippyInstance.set}
        onHide={handleOnHide}
        ref={ref}
        trigger={enhancedTrigger}
        maxWidth="none"
        size={size}
      >
        <ItemList role="menu">{mapChildren()}</ItemList>
      </StyledDropdown>
    );
  },
) as CompoundedDropdownMenu;

DropdownMenu.BaseItem = DropdownBaseItem;
DropdownMenu.Item = DropdownItem;
DropdownMenu.LinkItem = DropdownLinkItem;
DropdownMenu.Section = DropdownSection;
DropdownMenu.displayName = 'DropdownMenu';

// storybook will pick up defaults from this
DropdownMenu.defaultProps = {
  size: 's',
  isDisabled: false,
  triggerEvent: 'click',
  appendTo: () => document.body,
};
